import type { Schemas } from "@acervo/api-client";
import { AlertTriangle, Check, Copy, Loader2, Lock, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { errorMessage, visibilityLabel } from "../lib/format";

export function Logo({ className = "" }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2 font-display text-lg font-bold tracking-tight ${className}`}>
      <svg viewBox="0 0 32 32" className="h-7 w-7" aria-hidden>
        <rect width="32" height="32" rx="7" className="fill-raised" />
        <path
          d="M9 23 16 8l7 15M11.8 18h8.4"
          className="stroke-accent"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Acervo
    </span>
  );
}

export function Spinner({ label = "Carregando…" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-16 text-sm text-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {label}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex items-start gap-3 rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-fg">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
      <div className="flex-1">{errorMessage(error)}</div>
      {onRetry && (
        <button className="text-xs font-semibold text-accent hover:underline" onClick={onRetry}>
          Tentar de novo
        </button>
      )}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="card flex flex-col items-center gap-2 px-6 py-14 text-center">
      <p className="font-display text-lg">{title}</p>
      {children && <div className="max-w-md text-sm text-muted">{children}</div>}
    </div>
  );
}

export function ProgressBar({ value, className = "" }: { value: number; className?: string }) {
  return (
    <div
      className={`h-1 overflow-hidden rounded-full bg-white/10 ${className}`}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="h-full rounded-full bg-accent" style={{ width: `${value}%` }} />
    </div>
  );
}

/** Capa do curso: imagem, se houver; senão um degradê estável gerado do título. */
export function Cover({ title, url, className = "" }: { title: string; url?: string | null; className?: string }) {
  if (url) return <img src={url} alt="" loading="lazy" className={`object-cover ${className}`} />;
  let h = 0;
  for (const c of title) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const hue = h % 360;
  const initials = title
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  return (
    <div
      aria-hidden
      className={`flex items-end p-3 ${className}`}
      style={{
        background: `radial-gradient(120% 90% at 100% 0%, hsl(${hue} 70% 32%) 0%, transparent 60%), linear-gradient(135deg, hsl(${(hue + 40) % 360} 35% 16%), #0b0d10)`,
      }}
    >
      <span className="font-display text-3xl font-bold text-white/85">{initials || title.slice(0, 2)}</span>
    </div>
  );
}

export function VisibilityBadge({ course }: { course: Pick<Schemas["CourseCard"], "rights" | "visibility"> }) {
  return (
    <span className="flex flex-wrap gap-1">
      {course.rights === "third_party" && (
        <span className="badge bg-white/10 text-muted" title="Conteúdo de terceiros: só você vê">
          <Lock className="h-3 w-3" aria-hidden /> Pessoal
        </span>
      )}
      {course.rights === "owned" && (
        <span
          className={`badge ${course.visibility === "published" ? "bg-ok/15 text-ok" : "bg-accent/15 text-accent"}`}
        >
          {visibilityLabel[course.visibility]}
        </span>
      )}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d || d.open) return;
    if (typeof d.showModal === "function") d.showModal();
    else d.setAttribute("open", ""); // ambiente sem <dialog> modal (testes)
  }, []);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      aria-label={title}
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-xl border border-line bg-surface p-0 text-fg backdrop:bg-black/70"
    >
      <div className="flex items-center justify-between border-b border-line px-5 py-3">
        <h2 className="text-lg font-bold">{title}</h2>
        <button className="rounded p-1 text-muted hover:text-fg" onClick={onClose} aria-label="Fechar">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </dialog>
  );
}

/** Valor exibido uma única vez (senha provisória, códigos de recuperação), com botão de copiar. */
export function SecretBox({ value, hint }: { value: string; hint?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-lg border border-accent/40 bg-accent/5 p-3">
        <code className="flex-1 font-mono text-sm break-all whitespace-pre-wrap text-accent">{value}</code>
        <button
          className="btn-ghost px-2 py-1"
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            setCopied(true);
          }}
          aria-label="Copiar"
        >
          {copied ? <Check className="h-4 w-4 text-ok" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: "danger" | "ok" }) {
  return (
    <div className="card p-4">
      <p className="text-xs font-medium tracking-wide text-muted uppercase">{label}</p>
      <p
        className={`mt-1 font-display text-3xl font-bold ${tone === "danger" ? "text-danger" : tone === "ok" ? "text-ok" : ""}`}
      >
        {value}
      </p>
    </div>
  );
}
